import { hexToRGB } from '@repo-level/core-front';
import styled from 'styled-components/macro';
import { device } from 'components/styles/MediaQuery';
import arrowLeft from 'assets/images/arrowLeft.svg';
import arrowRight from 'assets/images/arrowRight.svg';

export interface ScContainerProps {
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

export const ScContainer = styled.div<ScContainerProps>`
  padding-left: ${({ paddingLeft }) =>
    paddingLeft == null ? `16px` : `${paddingLeft}px`};
  padding-right: ${({ paddingRight }) =>
    paddingRight == null ? `16px` : `${paddingRight}px`};
  padding-top: ${({ paddingTop }) =>
    paddingTop == null ? `12px` : `${paddingTop}px`};
  padding-bottom: ${({ paddingBottom }) =>
    paddingBottom == null ? `12px` : `${paddingBottom}px`};
  @media (${device.mobile}) {
    padding-top: ${({ paddingTop }) =>
      paddingTop == null ? `27px` : `${paddingTop}px`};
  }
  .lng-switch {
    width: max-content;
    min-width: 160px;
    margin: 48px auto 0 auto;
  }
`;

export const ScWidePaddingsContainer = styled.div`
  padding: 32px 64px 12px;
  flex: auto;

  @media (${device.mobile}) {
    padding: 16px 16px 12px;
  }
`;

export const ScStaticPage = styled.div`
  font-size: 16px;
  line-height: 1.5;
  padding: 12px 32px 0;
  color: ${({ theme }) => theme.secondaryColor6};

  & > div,
  .bonus-descr {
    margin-top: 24px;
  }

  & > .bonus-descr {
    & > p {
      margin: 0;
      line-height: 32px;
    }
  }
  .divider {
    display: none;
    @media (${device.mobile}) {
      display: block;
      margin: 0 auto;
      width: fit-content;
      margin-top: 24px;
      & > svg {
        display: block;
      }
    }
  }
  .date {
    margin-bottom: 48px;
    p {
      font-size: 12px;
      line-height: 1.5;
      text-align: center;
      margin: 0;
      span {
        color: ${({ theme }) => theme.mainColor};

        @media print {
          color: ${({ theme }) => theme.blackColor};
        }
      }
    }
  }
  h1,
  h2 {
    color: ${({ theme }) => theme.whiteColor};
    font-weight: 700;
    letter-spacing: 0.01em;
    text-align: center;
  }
  h1 {
    font-size: 22px;
    line-height: 1.27;
    margin-top: 12px;
    margin-bottom: 24px;
    & + p {
      color: ${({ theme }) => theme.whiteColor};
      text-align: center;
      max-width: 600px;
      margin: 24px auto 0;
    }
    @media (${device.mobile}) {
      margin-top: 24px;
    }
  }
  h2 {
    align-self: center;
    position: relative;
    font-size: 16px;
    line-height: 1.25;
    width: fit-content;
    margin: 48px auto 0;
    max-width: calc(100% - 140px);
    &:first-child {
      margin-top: 0;
    }
    &:before {
      content: '';
      position: absolute;
      right: calc(100% + 16px);
      top: 50%;
      transform: translate(0%, -50%);
      background: url(${arrowLeft}) center no-repeat;
      width: 56px;
      height: 16px;
    }
    &:after {
      content: '';
      position: absolute;
      left: calc(100% + 16px);
      top: 50%;
      transform: translate(0%, -50%);
      background: url(${arrowRight}) center no-repeat;
      width: 56px;
      height: 16px;
    }
    @media (${device.mobile}) {
      &.payments-title {
        margin-top: 24px;
      }
    }
  }
  h3,
  h4,
  h5,
  h6 {
    color: ${({ theme }) => theme.secondaryColor};
    font-weight: bold;
    font-size: 16px;
    margin-top: 48px;
    line-height: 1.25;
    letter-spacing: 0.01em;
    &:first-child {
      margin-top: 0;
    }
  }
  p {
    margin: 24px 0 0 0;
    font-size: 16px;
    line-height: 1.75;
    &:first-child {
      margin: 0;
    }
  }
  ul,
  ol {
    margin: 24px 0 0 1.5em;
    line-height: 2;
  }
  a {
    color: ${({ theme }) => theme.greenColor};
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }

  @media (${device.mobile}) {
    padding: 0;
    h1 {
      font-weight: 700;
      font-size: 18px;
      line-height: 1.33;
      margin-bottom: 24px;
      letter-spacing: 0.01em;
    }
  }

  ul,
  ol,
  p,
  h1,
  h2,
  h3,
  h4 {
    @media print {
      color: ${({ theme }) => theme.blackColor};
    }
  }

  .horizontal-scroll {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    border: 1px solid ${({ theme }) => theme.secondaryColor};
    padding: 8px;
    text-align: left;
    font-weight: 400;
  }
`;

export const ScLockElement = styled.div`
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    background: ${({ theme }) => hexToRGB(theme.secondaryColor3, 0.8)};
    border-radius: 4px;
    cursor: not-allowed;
  }
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    background: ${({ theme }) => `url(${theme.lockIcon}) center no-repeat`};
    background-size: cover;
    width: 10px;
    height: 15px;
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ScLobbyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 64px;
  align-items: center;
  width: 100%;
  position: relative;

  @media (${device.mobile}) {
    gap: 32px;
  }
`;

export const ScCMSTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  color: ${({ theme }) => theme.whiteColor};
  font-size: 14px;
  line-height: 20px;

  & h1,
  & h2 {
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: 0.01em;
    text-align: center;

    @media (${device.mobile}) {
      font-size: 18px;
      line-height: 24px;
    }
  }

  & h3 {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0.01em;
  }

  & p,
  & ul,
  & ol {
    & + h1,
    & + h2,
    & + h3 {
      margin: 16px 0;
    }
  }

  & p {
    & + ul,
    & + ol {
      margin-top: 32px;
    }
  }

  & h1,
  & h2,
  & h3 {
    & + p {
      margin-top: 16px;
    }
  }

  & ul,
  & ol {
    padding-left: 24px;

    & li {
      line-height: 28px;
      counter-increment: list-0;
      counter-reset: list-1 list-2 list-3;

      &.ql-indent-1 {
        padding-left: 20px;
        counter-increment: list-1;
        counter-reset: list-2;
        list-style-type: none;

        &:before {
          content: counter(list-1, lower-alpha) ') ';
        }
      }

      &.ql-indent-2 {
        padding-left: 40px;
        counter-increment: list-2;
        counter-reset: list-3;
        list-style-type: none;

        &:before {
          content: counter(list-2, lower-roman) '. ';
        }
      }
    }

    & + p {
      margin-top: 32px;
    }
  }

  & a {
    font-size: 14px;
    line-height: 20px;
    text-decoration-line: underline;
    color: ${({ theme }) => theme.greenColor};
    transition: opacity 0.2s ease-in-out;

    @media (hover: hover) {
      &:hover {
        opacity: 0.6;
      }
    }
  }
`;
